import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import WorkerSignupLayout, {
  NAVY, AMBER, WHITE, FONT_HEAD, FONT_BODY, StepPills,
} from './WorkerSignupLayout';

const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

const sectionLabel = {
  fontSize: '0.78rem', fontWeight: 700, color: NAVY,
  letterSpacing: '0.08em', textTransform: 'uppercase',
  marginBottom: '0.85rem',
};

const divider = { borderTop: '1px solid #EEF0F3', margin: '1.5rem 0' };

function ReviewRow({ label, value }) {
  const { t } = useTranslation();
  return (
    <div style={{ marginBottom: '0.85rem' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(13,27,42,0.45)', marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{ fontSize: '0.95rem', color: NAVY, fontWeight: 500 }}>
        {value || <span style={{ color: 'rgba(13,27,42,0.3)', fontStyle: 'italic' }}>{t('worker.signup.step5.notProvided')}</span>}
      </div>
    </div>
  );
}

export default function WorkerStep5() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) { navigate('/worker/signup'); return; }

      const [
        { data: worker },
        { data: workerProfile },
        { data: workerTrades },
        { data: certs },
        { data: licenses },
      ] = await Promise.all([
        supabase.from('workers').select('first_name, last_name, email, phone, zip_code').eq('id', userId).maybeSingle(),
        supabase.from('worker_profiles').select('city, state, bio, veteran_status').eq('id', userId).maybeSingle(),
        supabase.from('worker_trades').select('trade, experience_level, years_experience, min_hourly_rate, target_hourly_rate, availability, union_interest').eq('worker_id', userId).maybeSingle(),
        supabase.from('worker_certifications').select('certification_name, expiry_date').eq('worker_id', userId),
        supabase.from('worker_licenses').select('license_name, expiry_date').eq('worker_id', userId),
      ]);

      setProfile({
        worker,
        workerProfile,
        workerTrades,
        certs: certs || [],
        licenses: licenses || [],
      });
      setLoading(false);
    };
    fetchProfile();
  }, []); // eslint-disable-line

  const p = profile;

  return (
    <WorkerSignupLayout
      step={5}
      tipText={t('worker.signup.step5.tip')}
      onContinue={() => navigate('/worker/dashboard')}
      continueLabel={t('worker.signup.step5.continueLabel')}
    >
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('worker.signup.step5.eyebrow')}
      </div>

      <StepPills current={5} />

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('worker.signup.step5.title')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '2rem', lineHeight: 1.5 }}>
          {t('worker.signup.step5.subtitle')}
        </p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem 0', color: 'rgba(13,27,42,0.4)', fontSize: '0.9rem' }}>
            {t('worker.signup.step5.loading')}
          </div>
        ) : (
          <>
            {/* ── Account ── */}
            <div style={sectionLabel}>{t('worker.signup.step5.sectionAccount')}</div>
            <ReviewRow
              label={t('worker.signup.step5.nameLabel')}
              value={[p.worker?.first_name, p.worker?.last_name].filter(Boolean).join(' ')}
            />
            <ReviewRow label={t('worker.signup.step5.emailLabel')} value={p.worker?.email} />
            <ReviewRow label={t('worker.signup.step5.phoneLabel')} value={p.worker?.phone} />
            <ReviewRow label={t('worker.signup.step5.zipLabel')}   value={p.worker?.zip_code} />

            <div style={divider} />

            {/* ── Personal info ── */}
            <div style={sectionLabel}>{t('worker.signup.step5.sectionPersonalInfo')}</div>
            <ReviewRow
              label={t('worker.signup.step5.locationLabel')}
              value={[p.workerProfile?.city, p.workerProfile?.state].filter(Boolean).join(', ')}
            />
            <ReviewRow label={t('worker.signup.step5.bioLabel')}            value={p.workerProfile?.bio} />
            <ReviewRow label={t('worker.signup.step5.veteranStatusLabel')}  value={p.workerProfile?.veteran_status} />

            <div style={divider} />

            {/* ── Trade ── */}
            <div style={sectionLabel}>{t('worker.signup.step5.sectionTrade')}</div>
            <ReviewRow label={t('worker.signup.step5.tradesLabel')}            value={p.workerTrades?.trade} />
            <ReviewRow label={t('worker.signup.step5.experienceLevelLabel')}   value={p.workerTrades?.experience_level} />
            <ReviewRow label={t('worker.signup.step5.yearsExperienceLabel')}   value={p.workerTrades?.years_experience} />
            <ReviewRow label={t('worker.signup.step5.availabilityLabel')}      value={p.workerTrades?.availability} />
            <ReviewRow label={t('worker.signup.step5.unionInterestLabel')}     value={p.workerTrades?.union_interest} />
            {(p.workerTrades?.min_hourly_rate || p.workerTrades?.target_hourly_rate) && (
              <ReviewRow
                label={t('worker.signup.step5.payRangeLabel')}
                value={
                  p.workerTrades.min_hourly_rate && p.workerTrades.target_hourly_rate
                    ? `$${p.workerTrades.min_hourly_rate} – $${p.workerTrades.target_hourly_rate} / hr`
                    : `$${p.workerTrades.min_hourly_rate || p.workerTrades.target_hourly_rate} / hr`
                }
              />
            )}

            <div style={divider} />

            {/* ── Certifications ── */}
            <div style={sectionLabel}>{t('worker.signup.step5.sectionCertifications')}</div>
            {p.certs.length > 0 ? (
              p.certs.map((c, i) => (
                <div key={i} style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <span style={{ fontSize: '0.95rem', color: NAVY, fontWeight: 500 }}>{c.certification_name}</span>
                  {c.expiry_date && (
                    <span style={{ fontSize: '0.8rem', color: 'rgba(13,27,42,0.4)' }}>{t('worker.signup.step5.expires', { date: c.expiry_date })}</span>
                  )}
                </div>
              ))
            ) : (
              <div style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.35)', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                {t('worker.signup.step5.noneAdded')}
              </div>
            )}

            <div style={divider} />

            {/* ── Licenses ── */}
            <div style={sectionLabel}>{t('worker.signup.step5.sectionLicenses')}</div>
            {p.licenses.length > 0 ? (
              p.licenses.map((l, i) => (
                <div key={i} style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <span style={{ fontSize: '0.95rem', color: NAVY, fontWeight: 500 }}>{l.license_name}</span>
                  {l.expiry_date && (
                    <span style={{ fontSize: '0.8rem', color: 'rgba(13,27,42,0.4)' }}>{t('worker.signup.step5.expires', { date: l.expiry_date })}</span>
                  )}
                </div>
              ))
            ) : (
              <div style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.35)', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                {t('worker.signup.step5.noneAdded')}
              </div>
            )}
          </>
        )}
      </div>

      <p style={{ fontSize: '0.78rem', color: 'rgba(13,27,42,0.42)', lineHeight: 1.6, padding: '0 0.25rem', marginBottom: '0.5rem' }}>
        {t('worker.signup.step5.footerNote')}
      </p>
    </WorkerSignupLayout>
  );
}
